import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  sub: string;
  email: string;
  email_verified?: boolean;
  phone?: string;
  role?: string;
  iat?: number;
  exp?: number;
  name?: string;
  user_metadata?: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    user_name?: string;
    language?: string;
    password_set?: boolean;
    profile?: {
      country?: string;
      city?: string;
      zipCode?: string;
      bio?: string;
      profileImage?: string;
      coverImage?: string;
    };
    earthid_verification?: { is_verified?: boolean; [key: string]: unknown };
    earthIdVerification?: { is_verified?: boolean; [key: string]: unknown };
  };
  app_metadata?: { provider?: string };
  raw_user_meta_data?: {
    earthid_verification?: { is_verified?: boolean; [key: string]: unknown };
  };
}

interface CachedDecodedToken {
  decoded: DecodedToken;
  token: string;
  cachedAt: number;
}

let cachedToken: CachedDecodedToken | null = null;
const CACHE_TTL_MS = 30000;

export function decodeToken(token: string | null): DecodedToken | null {
  if (!token) return null;

  if (cachedToken && cachedToken.token === token && Date.now() - cachedToken.cachedAt < CACHE_TTL_MS) {
    return cachedToken.decoded;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    cachedToken = { decoded, token, cachedAt: Date.now() };
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

export function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  const decoded = decodeToken(token);
  if (!decoded?.exp) return false;
  return decoded.exp > Date.now() / 1000;
}

export function isTokenExpiringSoon(token: string | null, bufferSeconds = 60): boolean {
  if (!token) return true;
  const decoded = decodeToken(token);
  if (!decoded?.exp) return true;
  return decoded.exp < Date.now() / 1000 + bufferSeconds;
}

export function getTokenTimeRemaining(token: string | null): number {
  if (!token) return 0;
  const decoded = decodeToken(token);
  if (!decoded?.exp) return 0;
  return Math.max(0, decoded.exp - Date.now() / 1000);
}

export function clearTokenCache(): void {
  cachedToken = null;
}

export type { DecodedToken };
